import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Square, Wind } from 'lucide-react';
import { Howl } from 'howler';
import axios from 'axios';
import { SPORT_BREATHING_PROGRAMS } from './data';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  cyan: { border: 'border-cyan-400/50', bg: 'bg-cyan-500/15', text: 'text-cyan-300', circle: 'from-cyan-400/60 to-blue-500/60' },
  orange: { border: 'border-orange-400/50', bg: 'bg-orange-500/15', text: 'text-orange-300', circle: 'from-orange-400/60 to-red-500/60' },
  emerald: { border: 'border-emerald-400/50', bg: 'bg-emerald-500/15', text: 'text-emerald-300', circle: 'from-emerald-400/60 to-teal-500/60' }
};

const BreathingSportModule = ({ voiceGender, coachVoiceMap = {} }) => {
  const [program, setProgram] = useState(SPORT_BREATHING_PROGRAMS[0]);
  const [isRunning, setIsRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const voiceUrlsRef = useRef({});
  const voiceHowlRef = useRef(null);
  const intervalRef = useRef(null);
  const stateRef = useRef({ phaseIndex: 0, phaseElapsed: 0, totalElapsed: 0 });

  useEffect(() => {
    // Coach mode: use the coach's own recordings for the cues when available
    if (voiceGender === 'coach') {
      const urls = {};
      ['inhale', 'exhale', 'hold'].forEach(a => {
        const rec = coachVoiceMap[`respiration_${a}`];
        if (rec) urls[a] = rec.audio_url;
      });
      if (Object.keys(urls).length > 0) {
        voiceUrlsRef.current = urls;
        return;
      }
    }
    const items = [
      { id: 'inhale', text: 'Inspirez' },
      { id: 'exhale', text: 'Expirez' },
      { id: 'hold', text: 'Retenez' }
    ];
    axios.post(`${API}/api/edge-tts/generate`, { items, voice: voiceGender === 'coach' ? 'female' : voiceGender })
      .then(res => { voiceUrlsRef.current = res.data.voices || {}; })
      .catch(() => {});
  }, [voiceGender, coachVoiceMap]);

  const playPhaseVoice = useCallback((action) => {
    const url = voiceUrlsRef.current[action];
    if (!url) return;
    if (voiceHowlRef.current) { voiceHowlRef.current.stop(); voiceHowlRef.current.unload(); }
    voiceHowlRef.current = new Howl({ src: [`${API}${url}`], html5: true, volume: 0.9 });
    voiceHowlRef.current.play();
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (voiceHowlRef.current) { voiceHowlRef.current.stop(); voiceHowlRef.current.unload(); voiceHowlRef.current = null; }
    setIsRunning(false);
    setPhaseIndex(0);
    setElapsed(0);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = () => {
    stop();
    stateRef.current = { phaseIndex: 0, phaseElapsed: 0, totalElapsed: 0 };
    setIsRunning(true);
    setPhaseIndex(0);
    setPhaseTimeLeft(program.phases[0].duration);
    playPhaseVoice(program.phases[0].action);
    intervalRef.current = setInterval(() => {
      const st = stateRef.current;
      st.phaseElapsed += 0.1;
      st.totalElapsed += 0.1;
      const currentPhase = program.phases[st.phaseIndex];
      if (st.totalElapsed >= program.durationMin * 60) {
        stop();
        return;
      }
      if (st.phaseElapsed >= currentPhase.duration) {
        st.phaseIndex = (st.phaseIndex + 1) % program.phases.length;
        st.phaseElapsed = 0;
        setPhaseIndex(st.phaseIndex);
        playPhaseVoice(program.phases[st.phaseIndex].action);
      }
      setPhaseTimeLeft(Math.max(0, Math.ceil(program.phases[st.phaseIndex].duration - st.phaseElapsed)));
      setElapsed(st.totalElapsed);
    }, 100);
  };

  const currentPhase = program.phases[phaseIndex];
  const colors = COLOR[program.color];
  const isInflated = isRunning && (currentPhase.action === 'inhale' || currentPhase.action === 'hold');

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="space-y-4" data-testid="sport-breathing-module">
      <div className="grid grid-cols-1 gap-2">
        {SPORT_BREATHING_PROGRAMS.map(p => {
          const c = COLOR[p.color];
          return (
            <button key={p.id} onClick={() => { stop(); setProgram(p); }}
              className={`p-3 rounded-xl text-left transition-all border ${
                program.id === p.id ? `${c.bg} ${c.border}` : 'bg-white/5 border-transparent hover:bg-white/10'
              }`}
              data-testid={`breathing-program-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${program.id === p.id ? c.text : 'text-white/80'}`}>{p.name}</p>
                  <p className="text-white/50 text-xs">{p.desc}</p>
                </div>
                <div className="flex items-center gap-1">
                  {p.phases.map((ph, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded bg-white/10 text-white/50 text-[10px]">{ph.duration}s</span>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-4 py-4">
        <div className="relative w-40 h-40 flex items-center justify-center">
          <div
            className={`absolute inset-0 rounded-full bg-gradient-to-br ${colors.circle} blur-sm`}
            style={{
              transform: isInflated ? 'scale(1.15)' : 'scale(0.75)',
              transition: `transform ${isRunning ? currentPhase.duration : 0.5}s ease-in-out`
            }}
          />
          <div className="relative z-10 text-center">
            {isRunning ? (
              <>
                <p className="text-white text-xl font-heading font-semibold" data-testid="breathing-phase-label">{currentPhase.label}</p>
                <p className="text-white/70 text-2xl font-heading">{phaseTimeLeft}s</p>
              </>
            ) : (
              <Wind className="w-10 h-10 text-white/80 mx-auto" />
            )}
          </div>
        </div>
        {isRunning && (
          <p className="text-white/50 text-sm">{fmt(elapsed)} / {program.durationMin}:00</p>
        )}
        {!isRunning ? (
          <button onClick={start}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold hover:scale-105 transition-transform shadow-xl flex items-center gap-2"
            data-testid="start-sport-breathing">
            <Play className="w-5 h-5" /> Commencer
          </button>
        ) : (
          <button onClick={stop}
            className="px-8 py-3.5 rounded-full bg-white/15 text-white font-semibold hover:bg-white/25 transition-all flex items-center gap-2"
            data-testid="stop-sport-breathing">
            <Square className="w-5 h-5" /> Arrêter
          </button>
        )}
      </div>
    </div>
  );
};

export default BreathingSportModule;
